import { daysBetween, fromIso, shiftDate } from '../../core/format/dates'
import type { DeviceKind, OwnLink } from '../../core/types/insights'
import type { StopReasonCode } from '../../core/types/requests'

/**
 * DEMO — voorbeeldcijfers voor Inzicht. Wat de echte server uit externe bronnen ophaalt
 * (Umami, Search Console, Instagram, Brevo) en een deel van de eigen statistiek bestaat in de
 * demo niet, dus die maken we hier: vast per datum (geen toeval bij elke klik), met een
 * weekritme en een piek op de dag na een Instagram-post.
 */

/** Vast getal tussen 0 en 1 per sleutel. */
export function noise(key: string): number {
  let h = 5381
  for (let i = 0; i < key.length; i++) h = (h * 33) ^ key.charCodeAt(i)
  return ((h >>> 0) % 10_000) / 10_000
}

/** Alle dagen van `from` tot en met `to`. */
export const daysOf = (from: string, to: string) => Array.from({ length: Math.max(0, daysBetween(from, to) + 1) }, (_, i) => shiftDate(from, i))

/** Een getal per dag, opgeteld over de periode, met wat spreiding. */
export const scaled = (from: string, to: string, perDay: number, key: string) =>
  Math.round(daysOf(from, to).length * perDay * (0.85 + noise(`${key}${from}${to}`) * 0.3))

/** Sarah post op di, do en za, niet elke week alle drie. */
export const isPostDay = (date: string) => [2, 4, 6].includes(fromIso(date).getDay()) && noise(`post${date}`) < 0.55

/** Bezoeken op een dag: zondag rustig, zaterdag druk, de dag na een post meer. */
export function visitsOn(date: string): number {
  const base = [48, 72, 78, 76, 82, 90, 98][fromIso(date).getDay()]
  const spike = isPostDay(shiftDate(date, -1)) ? 1.6 + noise(`spike${date}`) * 1.2 : 1
  return Math.round(base * (0.8 + noise(`v${date}`) * 0.4) * spike)
}

export const pageShares: { path: string; share: number }[] = [
  { path: '/', share: 0.27 },
  { path: '/behandelingen', share: 0.16 },
  { path: '/webshop', share: 0.14 },
  { path: '/webshop/retinal-1', share: 0.06 },
  { path: '/journal/rosacea-en-roodheid', share: 0.05 },
  { path: '/routines', share: 0.04 },
  { path: '/over-ons', share: 0.03 },
  { path: '/contact', share: 0.03 },
]

export const sourceShares: { source: string; label: string; share: number }[] = [
  { source: 'google', label: 'Google', share: 0.34 },
  { source: 'instagram', label: 'Instagram', share: 0.29 },
  { source: 'direct', label: 'Direct', share: 0.21 },
  { source: 'nieuwsbrief', label: 'Nieuwsbrief', share: 0.06 },
  { source: 'instagram-bio', label: 'Eigen link: Instagram-bio', share: 0.05 },
  { source: 'flyer-salon', label: 'Eigen link: flyer salon', share: 0.03 },
  { source: 'kaartje', label: 'Eigen link: afsprakenkaartje', share: 0.02 },
]

export const deviceShares: { device: DeviceKind; share: number }[] = [
  { device: 'mobiel', share: 0.71 },
  { device: 'desktop', share: 0.24 },
  { device: 'tablet', share: 0.05 },
]

/** Per 30 dagen; "rosacea" heeft nog geen product of artikel dat gevonden wordt. */
export const searchSeed: { raw: string; per30: number; results: number }[] = [
  { raw: 'Retinol', per30: 22, results: 4 },
  { raw: 'rosacea', per30: 14, results: 0 },
  { raw: 'vitamine c', per30: 11, results: 3 },
  { raw: 'SPF', per30: 9, results: 2 },
  { raw: 'pigmentvlekken', per30: 7, results: 0 },
  { raw: 'cadeaubon', per30: 6, results: 1 },
  { raw: 'microneedling', per30: 5, results: 1 },
  { raw: 'acne littekens', per30: 4, results: 0 },
  { raw: 'jan@voorbeeld.nl', per30: 1, results: 0 },
]

export const querySeed: { query: string; per30: number; ctr: number; position: number; trend: number }[] = [
  { query: 'schoonheidssalon utrecht', per30: 820, ctr: 0.041, position: 6.2, trend: -1.4 },
  { query: 'beauty bewust', per30: 310, ctr: 0.42, position: 1.1, trend: 0 },
  { query: 'microneedling utrecht', per30: 260, ctr: 0.058, position: 4.8, trend: -3.1 },
  { query: 'rosacea behandeling', per30: 240, ctr: 0.012, position: 11.4, trend: 2.6 },
  { query: 'retinal serum', per30: 190, ctr: 0.021, position: 9.3, trend: -0.4 },
  { query: 'huidanalyse', per30: 150, ctr: 0.034, position: 7.7, trend: 0.8 },
  { query: 'emerginc kopen', per30: 95, ctr: 0.09, position: 3.2, trend: -4.2 },
]

export const searchPageSeed: { path: string; title: string; per30: number; ctr: number; editTo: string | null }[] = [
  { path: '/', title: 'Homepage', per30: 1450, ctr: 0.062, editTo: '/teksten' },
  { path: '/behandelingen', title: 'Behandelingen', per30: 980, ctr: 0.031, editTo: '/teksten' },
  { path: '/journal/rosacea-en-roodheid', title: 'Rosacea en roodheid', per30: 720, ctr: 0.006, editTo: '/journal' },
  { path: '/webshop/retinal-1', title: 'Retinal 1', per30: 410, ctr: 0.019, editTo: '/producten' },
  { path: '/webshop', title: 'Webshop', per30: 380, ctr: 0.028, editTo: '/teksten' },
]

export const captions = [
  'Waarom je huid in de herfst om meer vraagt',
  'Achter de schermen: een huidanalyse',
  'Drie fouten met retinol die ik vaak zie',
  'Resultaat na zes microneedling-sessies',
  'Zo breng je SPF aan, ook onder make-up',
  'Nieuw binnen: het vitamine C-serum',
  'Een dag in de salon',
  'Rosacea: wat helpt en wat juist niet',
]

export const newsletterSubjects = [
  'Je huid in oktober',
  'Nieuw in de salon: huidanalyse',
  'Drie tips voor een rustige huid',
  'Cadeautip voor de feestdagen',
  'Wat ik deze maand gebruik',
]

/** Per vaste reden (core/rules/stopReasons.ts); `null` = zonder reden opgezegd. */
export const stopReasons: { code: StopReasonCode | null; weight: number }[] = [
  { code: 'genoeg', weight: 0.35 },
  { code: 'te-duur', weight: 0.2 },
  { code: 'iets-anders', weight: 0.15 },
  { code: 'huid', weight: 0.05 },
  { code: 'anders', weight: 0.1 },
  { code: null, weight: 0.15 },
]

export const ownLinks: Omit<OwnLink, 'visits'>[] = [
  { id: 'link-bio', label: 'Instagram-bio', path: '/', utmSource: 'instagram-bio' },
  { id: 'link-flyer', label: 'Flyer in de salon', path: '/behandelingen', utmSource: 'flyer-salon' },
  { id: 'link-kaartje', label: 'Afsprakenkaartje', path: '/webshop', utmSource: 'kaartje' },
]

/** Namen voor wegglijdende gasten zonder account (alleen in de demo). */
export const slippingGuests = ['Mirjam de Groot', 'Fatma Yilmaz', 'Lotte Verbeek', 'Ingrid Smit', 'Noor Bakker', 'Sanne Hendriks', 'Petra van Dijk', 'Yara Mulder', 'Els Janssen', 'Kim de Wit']
